import API from "../api/api";
import { DistributorAccess } from "./AuthenticationService";
import { DistributorService } from "../services/DistributorService";
import { useDistributorStore } from "../store/distributorStore";
import { usePartnerStore } from "../store/partnerStore";

/**
 * selectDistributor
 * -----------------
 * Common bootstrap step shared by:
 *   - LoginPage (single-distributor auto-select)
 *   - DistributorSelectPage (user picks one of several)
 *
 * Exchanges the no-scope token for a distributor-scoped token and populates
 * the distributor + partner stores. Partner config is best-effort — a failure
 * logs and continues so the user still lands on a working dashboard.
 *
 * NOTE: this runs from event handlers, NOT React render, so it cannot use
 * React hooks. We avoid `new AuthenticationService()` here because that class
 * captures `useNavigate()` at construction time, which would throw "Invalid
 * hook call" if instantiated outside a component render.
 */

const TOKEN_KEY = "auth-token";

export async function selectDistributor(
  distributor: DistributorAccess
): Promise<void> {
  const distributorService = new DistributorService();
  const { setDistributorId, setDistributorName } = useDistributorStore.getState();
  const { setPartnerConfig } = usePartnerStore.getState();

  // 1. Exchange no-scope → distributor-scoped token (required)
  let scopedToken: string;
  try {
    const response = await API.get(
      `/auth/distributor-user/distributor-scoped-token?distributorId=${distributor.distributorId}`
    );
    scopedToken = response.data.token;
    API.defaults.headers.common["Authorization"] = `Bearer ${scopedToken}`;
    localStorage.setItem(TOKEN_KEY, scopedToken);
  } catch (e) {
    console.error("[selectDistributor] scoped-token exchange failed", e);
    throw e;
  }

  // 2. Distributor identity in store
  setDistributorId(distributor.distributorId);
  setDistributorName(distributor.distributorName);

  // 3. Partner config (best-effort). On failure, reset the partner store so
  //    we don't render the new dashboard with the previous distributor's
  //    currency/branding cached from an earlier session.
  try {
    const partnerConfig = await distributorService.getPartnerConfig();
    setPartnerConfig(partnerConfig);
  } catch (e) {
    console.warn(
      "[selectDistributor] partner config unavailable; resetting to defaults.",
      e
    );
    setPartnerConfig(null);
  }
}
